import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, MessageSquare, Share2 } from "lucide-react";
import { BrandInput } from "@/components/ui/BrandInput";
import { saveEmail, savePhoneNumber, n8nSaveEmail, n8nSavePhone, n8nSaveEmailSS, n8nSavePhoneSS, n8nSavePhoneSlickText, sendMetaLeadEvent } from "@/lib/firebase";
import { validateEmail, validatePhone } from "@/lib/validation";
import { canSubmit, recordSubmission, getSecondsRemaining } from "@/lib/rate-limit";
import logoWhite from "@/assets/logo-white.svg";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";

interface EarlyAccessModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  initialStep?: "email" | "phone";
}

const EarlyAccessModal = ({ isOpen: externalIsOpen, onClose: externalOnClose, initialStep = "email" }: EarlyAccessModalProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [step, setStep] = useState<"email" | "phone" | "done">(initialStep);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Determine if controlled externally or internally
  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;
  const setIsOpen = isControlled ? (val: boolean) => { if (!val && externalOnClose) externalOnClose(); } : setInternalIsOpen;

  useEffect(() => {
    return;
    // Only auto-show when not externally controlled
    if (isControlled) return;
    
    // Check if user has already dismissed or completed
    const dismissed = localStorage.getItem("yrd_modal_dismissed") === "true";
    const completed = localStorage.getItem("yrd_early_access_completed") === "true";
    
    if (dismissed || completed) return;

    // Show after 3 seconds
    const timer = setTimeout(() => {
      setInternalIsOpen(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [isControlled]);

  // Listen for manual trigger from buttons (for uncontrolled mode only)
  useEffect(() => {
    if (isControlled) return;
    const handleOpenModal = () => setInternalIsOpen(true);
    window.addEventListener('openEarlyAccess', handleOpenModal);
    return () => window.removeEventListener('openEarlyAccess', handleOpenModal);
  }, [isControlled]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    if (!canSubmit("email")) {
      setErrorMessage(`Please wait ${getSecondsRemaining("email")}s before submitting again.`);
      return;
    }
    const validationError = validateEmail(email);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await Promise.all([
        saveEmail(email, "early-access"),
        n8nSaveEmail(email, "early-access").catch(console.error),
        n8nSaveEmailSS(email, "early-access").catch(console.error),
        sendMetaLeadEvent({ email }, "early-access").catch(console.error),
      ]);
      localStorage.setItem("yrd_email_submitted", "true");
      recordSubmission("email");
      // Fire client-side pixel Lead event
      if (typeof window !== "undefined" && (window as any).fbq) {
        (window as any).fbq('track', 'Lead', { content_name: 'early-access-email' });
      }
      setSuccessMessage("Email saved!");
      
      // Move to phone step after brief delay
      setTimeout(() => {
        setStep("phone");
        setSuccessMessage(null);
      }, 800);
    } catch (error) {
      console.error("Error saving email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return;
    if (!canSubmit("phone")) {
      setErrorMessage(`Please wait ${getSecondsRemaining("phone")}s before submitting again.`);
      return;
    }
    const validationError = validatePhone(phone);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await Promise.all([
        savePhoneNumber(phone, "early-access"),
        n8nSavePhone(phone, "early-access").catch(console.error),
        n8nSavePhoneSS(phone, "early-access").catch(console.error),
        n8nSavePhoneSlickText(phone).catch(console.error),
        sendMetaLeadEvent({ phone }, "early-access").catch(console.error),
      ]);
      localStorage.setItem("yrd_phone_submitted", "true");
      recordSubmission("phone");
      // Fire client-side pixel Lead event
      if (typeof window !== "undefined" && (window as any).fbq) {
        (window as any).fbq('track', 'Lead', { content_name: 'early-access-phone' });
      }
      localStorage.setItem("yrd_early_access_completed", "true");
      setSuccessMessage("You're on the list!");
      setStep("done");
    } catch (error) {
      console.error("Error saving phone:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipPhone = () => {
    localStorage.setItem("yrd_early_access_completed", "true");
    if (externalOnClose) externalOnClose();
    else setInternalIsOpen(false);
  };

  const handleClose = () => {
    if (!isControlled) {
      localStorage.setItem("yrd_modal_dismissed", "true");
    }
    if (externalOnClose) externalOnClose();
    else setInternalIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative w-full max-w-md bg-yacht-blue rounded-3xl overflow-hidden shadow-2xl"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Background graphic */}
              <img
                src={boatsGraphic}
                alt=""
                className="absolute -bottom-20 -right-20 w-[300px] opacity-10 pointer-events-none"
                style={{ filter: "brightness(0) invert(1)" }}
              />

              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 text-white/30 hover:text-white/60 transition-colors z-20"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="relative z-10 p-8 pt-10">
                {/* Logo */}
                <div className="flex justify-center mb-6">
                  <img src={logoWhite} alt="Yacht Rock Detroit" className="h-16" />
                </div>

                {step === "done" ? (
                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      You're In! 🎉
                    </h2>
                    <p className="text-white/70 mb-6">
                      We'll hit you up first when early bird tickets drop.
                    </p>
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={async () => {
                          const shareData = {
                            title: 'Yacht Rock Detroit 2026',
                            text: 'Get early access to Yacht Rock Detroit 2026! Sign up for presale tickets.',
                            url: window.location.origin,
                          };
                          
                          if (navigator.share && navigator.canShare(shareData)) {
                            try {
                              await navigator.share(shareData);
                            } catch (err) {
                              // User cancelled or error
                              console.log('Share cancelled');
                            }
                          } else {
                            // Fallback: copy to clipboard
                            await navigator.clipboard.writeText(window.location.origin);
                            alert('Link copied to clipboard!');
                          }
                        }}
                        className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-yacht-yellow text-yacht-charcoal font-bold rounded-full hover:scale-105 transition-transform"
                      >
                        <Share2 className="w-4 h-4" />
                        Share with your crew
                      </button>
                      <button
                        onClick={() => setIsOpen(false)}
                        className="text-white/50 hover:text-white text-sm underline transition-colors"
                      >
                        Skip for now
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <>
                    {/* Headline */}
                    <div className="text-center mb-6">
                      <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        Get Early Bird Access
                      </h2>
                      <p className="text-white/70">
                        {step === "email" 
                          ? <>Be first in line for <span className="font-bold text-white">exclusive presale</span> tickets.</> 
                          : "One more step — get text alerts for instant updates."}
                      </p>
                    </div>

                    {/* Form */}
                    {step === "email" ? (
                      <motion.form
                        onSubmit={handleEmailSubmit}
                        className="space-y-4"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        key="email-form"
                      >
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yacht-charcoal/40" />
                          <input
                            type="email"
                            id="early-access-email"
                            name="email"
                            autoComplete="email"
                            inputMode="email"
                            placeholder="your@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full pl-12 pr-4 py-4 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-yellow text-lg"
                          />
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full py-4 bg-yacht-yellow text-yacht-charcoal font-bold text-lg rounded-full hover:scale-[1.02] transition-transform disabled:opacity-50"
                        >
                          {isSubmitting ? "Saving..." : "Get Early Access"}
                        </button>

                        {errorMessage && (
                          <p className="text-red-400 text-center text-sm font-medium">{errorMessage}</p>
                        )}
                        {successMessage && (
                          <p className="text-green-400 text-center font-medium">{successMessage}</p>
                        )}

                        <button
                          type="button"
                          onClick={() => setStep("phone")}
                          className="w-full text-white/50 hover:text-white text-sm underline transition-colors"
                        >
                          I'd rather sign up via SMS
                        </button>
                      </motion.form>
                    ) : (
                      <motion.form
                        onSubmit={handlePhoneSubmit}
                        className="space-y-4"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        key="phone-form"
                      >
                        <div className="relative">
                          <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yacht-charcoal/40" />
                          <input
                            type="tel"
                            id="early-access-phone"
                            name="phone"
                            autoComplete="tel"
                            inputMode="tel"
                            placeholder="(555) 123-4567"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            required
                            className="w-full pl-12 pr-4 py-4 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-yellow text-lg"
                          />
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full py-4 bg-yacht-yellow text-yacht-charcoal font-bold text-lg rounded-full hover:scale-[1.02] transition-transform disabled:opacity-50"
                        >
                          {isSubmitting ? "Saving..." : "Complete Signup"}
                        </button>

                        {successMessage && (
                          <p className="text-green-400 text-center font-medium">{successMessage}</p>
                        )}

                        {/* Only show back button if email hasn't been submitted yet */}
                        {localStorage.getItem("yrd_email_submitted") !== "true" && (
                          <button
                            type="button"
                            onClick={() => {
                              setStep("email");
                              setSuccessMessage(null);
                            }}
                            className="w-full text-white/50 hover:text-white text-sm underline transition-colors"
                          >
                            ← Back to email
                          </button>
                        )}
                      </motion.form>
                    )}
                  </>
                )}
              </div>

              {/* Bottom accent */}
              <div className="h-2 bg-yacht-yellow" />
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EarlyAccessModal;
